import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SeasonPassService } from 'src/app/services/season-pass.service';
import { SeasonPass } from 'src/app/interfaces/abonado';
import { Product } from 'src/app/interfaces/product';
import { AppSettings } from 'src/app/app-settings';
import { seasonPassSeatLabel } from 'src/app/helpers/season-pass-seat';

/**
 * Abono con el precio que le toca pagar. Puede haber varias opciones para su
 * categoría (con y sin carnet físico), y elegir es cosa del checkout, así que
 * aquí se muestra la más barata como "desde".
 */
interface SeasonPassView {
  seasonPass: SeasonPass;
  price: number | null;
  hasSeveralPrices: boolean;
  /** URL ya montada de la imagen del producto, o null para el placeholder. */
  image: string | null;
}

@Component({
  selector: 'app-my-membership',
  templateUrl: './my-membership.component.html',
  styleUrls: ['./my-membership.component.scss']
})
export class MyMembershipComponent implements OnInit {

  abonos: SeasonPassView[] = [];
  loaded: boolean = false;
  loadError: boolean = false;

  // La barra va aquí porque MEDIA_URI no la lleva en pre/prod (mismo convenio
  // que el formulario de producto del panel).
  private urlProductImages: string = AppSettings.MEDIA_URI + '/products_images/';

  constructor(
    private seasonPassService: SeasonPassService,
    private location: Location,
    private router: Router,
    private route: ActivatedRoute,
  ) { }

  /**
   * True cuando hay una subpágina activa (el carnet digital) en el
   * router-outlet: en ese caso se oculta el listado y se ve el carnet.
   */
  get carnetOpen(): boolean {
    return this.route.children.length > 0;
  }

  ngOnInit(): void {
    this.loadSeasonPasses();
  }

  private loadSeasonPasses() {
    this.loaded = false;
    this.loadError = false;

    this.seasonPassService.getMySeasonPasses().subscribe({
      next: (res) => {
        const seasonPasses: SeasonPass[] = res.data || [];

        if (!seasonPasses.length) {
          this.abonos = [];
          this.loaded = true;
          return;
        }

        // El precio vive en los productos de la temporada (por categoría y
        // nivel de butaca), no en el abono, así que hay que preguntarlos por
        // cada uno. Un abono sin butaca o sin producto configurado no debe
        // tumbar la lista: se muestra sin precio.
        forkJoin(seasonPasses.map(sp => this.loadPrice(sp))).subscribe({
          next: (abonos) => {
            this.abonos = abonos;
            this.loaded = true;
          }
        });
      },
      error: () => {
        this.abonos = [];
        this.loadError = true;
        this.loaded = true;
      }
    });
  }

  /**
   * El precio depende de la categoría del abonado Y del nivel de su butaca, y
   * quien resuelve ese nivel es el servidor, así que se pregunta por el
   * endpoint del abono y no por el de la temporada. Sin butaca asignada la
   * respuesta viene vacía y el abono se muestra sin precio.
   */
  private loadPrice(seasonPass: SeasonPass) {
    const companyId = seasonPass.season?.id_company;
    const empty: SeasonPassView = { seasonPass, price: null, hasSeveralPrices: false, image: null };

    if (!companyId) {
      return of(empty);
    }

    return this.seasonPassService.getAvailableProducts(companyId, seasonPass.id).pipe(
      map((res) => {
        const products: Product[] = (res?.data?.products || [])
          .filter((p: Product) => p.active !== false);
        const prices: number[] = products.map((p: Product) => +p.price);

        if (!prices.length) return empty;

        return {
          seasonPass,
          price: Math.min(...prices),
          hasSeveralPrices: new Set(prices).size > 1,
          image: this.productImageUrl(products),
        } as SeasonPassView;
      }),
      catchError(() => of(empty)),
    );
  }

  /**
   * Foto de la tarjeta: la del producto del abono. Las varias opciones que
   * puede haber para su categoría y nivel son variantes del mismo abono (con y
   * sin carnet físico), así que vale la primera que traiga imagen.
   */
  private productImageUrl(products: Product[]): string | null {
    const image = products.find(p => !!p.image)?.image;
    if (!image) return null;
    return image.startsWith('http') ? image : this.urlProductImages + image;
  }

  /** Si la imagen no está en el disco, se cae al placeholder en vez de al icono roto. */
  onImageError(abono: SeasonPassView) {
    abono.image = null;
  }

  isPaid(seasonPass: SeasonPass): boolean {
    return seasonPass.status?.slug === 'active';
  }

  /** El precio no se muestra para los abonos en estos estados. */
  private readonly hidePriceForStatusIds = [2, 3, 4, 5];

  showPrice(seasonPass: SeasonPass): boolean {
    const statusId = seasonPass.status?.id ?? seasonPass.id_status;
    return !this.hidePriceForStatusIds.includes(statusId);
  }

  seatDisplay(seasonPass: SeasonPass): string | null {
    return seasonPassSeatLabel(seasonPass);
  }

  goToCheckout(seasonPass: SeasonPass) {
    this.router.navigate(['/abono-checkout', seasonPass.id]);
  }

  /**
   * Abre la página del carnet digital (QR con el hash del abono). Solo para
   * abonos activos: sin pago confirmado no hay QR válido que mostrar.
   */
  openQr(seasonPass: SeasonPass) {
    if (!this.isPaid(seasonPass) || !seasonPass.hash) return;
    this.router.navigate(['/profile/abonos', seasonPass.id, 'carnet']);
  }

  goBack() {
    this.location.back();
  }
}
